/**
 * AdminCronRuns.js
 * Admin observability page for the cron_runs table (Item 1.4b-6).
 *
 * Renders the durable cron-run history (Item 1.4b's track_cron_run wrapper
 * writes one row per execution). Filters by cron_name + status + time_range;
 * sorted by started_at DESC. Inline-expandable row shows side_effects JSON,
 * full error_message, and (collapsed) error_traceback.
 *
 * Mirrors the existing admin page pattern: useAuth → Bearer JWT → fetch
 * /api/admin/cron-runs. Inline styles + adminColors palette. No new deps.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { Card } from '../components/SharedComponents';
import { API_BASE } from '../config';
import colors from '../utils/adminColors';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'success', label: 'Success' },
  { value: 'partial', label: 'Partial' },
  { value: 'failed', label: 'Failed' },
  { value: 'running', label: 'Running' },
];

const TIME_RANGE_OPTIONS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

const STATUS_COLOR = {
  success: colors.success,
  partial: colors.warning,
  failed: colors.danger,
  running: colors.accent,
};

const PAGE_SIZE = 50;

function formatTimestamp(iso) {
  if (!iso) return 'Not available';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatDuration(ms) {
  if (ms == null) return 'Not available';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const mins = Math.floor(ms / 60_000);
  const secs = ((ms % 60_000) / 1000).toFixed(0);
  return `${mins}m ${secs}s`;
}

function truncate(text, max = 80) {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
}

// Pretty-print a side_effects payload with light formatting (Discovery #4).
// Surfaces the "errors" array as its own block when non-empty so failed/partial
// runs are scannable without expanding the raw JSON.
function renderSideEffects(payload) {
  if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
    return <span style={{ color: colors.textMuted, fontStyle: 'italic' }}>empty</span>;
  }
  const errors = Array.isArray(payload?.errors) ? payload.errors : null;
  const rest = errors ? { ...payload } : payload;
  if (errors) delete rest.errors;

  return (
    <div>
      {errors && errors.length > 0 && (
        <div style={{
          marginBottom: '12px', padding: '8px 12px',
          backgroundColor: `${colors.danger}10`,
          borderLeft: `3px solid ${colors.danger}`,
          borderRadius: '4px',
        }}>
          <div style={{
            fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em',
            fontWeight: 600, color: colors.danger, marginBottom: '4px',
          }}>
            errors ({errors.length})
          </div>
          {errors.map((e, i) => (
            <div key={i} style={{
              fontSize: '13px', color: colors.darkText, fontFamily: 'Menlo, Consolas, monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: i > 0 ? '4px' : 0,
            }}>
              {typeof e === 'string' ? e : JSON.stringify(e)}
            </div>
          ))}
        </div>
      )}
      <pre style={{
        margin: 0, padding: '8px 12px', fontSize: '12px',
        backgroundColor: colors.lightGray, borderRadius: '4px',
        fontFamily: 'Menlo, Consolas, monospace',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: colors.darkText,
      }}>
        {JSON.stringify(rest, null, 2)}
      </pre>
    </div>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLOR[status] || colors.textMuted;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
      fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.04em', color: c, backgroundColor: `${c}15`,
      border: `1px solid ${c}40`,
    }}>
      {status}
    </span>
  );
}

function AdminCronRuns() {
  const { token } = useAuth();

  const [runs, setRuns] = useState([]);
  const [distinctNames, setDistinctNames] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterCronName, setFilterCronName] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTimeRange, setFilterTimeRange] = useState('7d');
  const [page, setPage] = useState(1);

  // Expansion: track which run_ids are expanded, plus per-row traceback toggle
  const [expanded, setExpanded] = useState(new Set());
  const [tracebackOpen, setTracebackOpen] = useState(new Set());

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        time_range: filterTimeRange,
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (filterCronName) params.set('cron_name', filterCronName);
      if (filterStatus) params.set('status', filterStatus);

      const resp = await fetch(`${API_BASE}/api/admin/cron-runs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setRuns(data.runs || []);
      setDistinctNames(data.distinct_cron_names || []);
      setPagination(data.pagination || { page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 0 });
    } catch (e) {
      setError(e.message || 'Failed to load cron runs');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [token, filterCronName, filterStatus, filterTimeRange, page]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Reset to page 1 whenever a filter changes
  useEffect(() => { setPage(1); }, [filterCronName, filterStatus, filterTimeRange]);

  const toggleExpanded = (runId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId); else next.add(runId);
      return next;
    });
  };

  const toggleTraceback = (runId) => {
    setTracebackOpen(prev => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId); else next.add(runId);
      return next;
    });
  };

  return (
    <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontSize: 'var(--font-size-hero)',
          fontWeight: 'var(--font-weight-bold)',
          color: colors.darkText,
          marginBottom: '8px',
        }}>
          Cron Runs
        </h1>
        <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', margin: 0 }}>
          Durable execution history of all scheduled jobs. Filter by job, status, and time window.
        </p>
      </div>

      {/* Filter bar */}
      <Card padding="16px 20px" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
          <FilterField label="Cron name">
            <select
              value={filterCronName}
              onChange={(e) => setFilterCronName(e.target.value)}
              style={selectStyle}
            >
              <option value="">All jobs</option>
              {distinctNames.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Status">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={selectStyle}
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Time range">
            <select
              value={filterTimeRange}
              onChange={(e) => setFilterTimeRange(e.target.value)}
              style={selectStyle}
            >
              {TIME_RANGE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </FilterField>

          <div style={{ marginLeft: 'auto', fontSize: '13px', color: colors.textMuted }}>
            {pagination.total} total · page {pagination.page} of {Math.max(1, pagination.total_pages)}
          </div>
        </div>
      </Card>

      {/* Table card */}
      <Card padding="0">
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: colors.textMuted }}>
            Loading cron runs…
          </div>
        ) : error ? (
          <div style={{
            padding: '20px 24px',
            backgroundColor: `${colors.danger}10`,
            borderLeft: `3px solid ${colors.danger}`,
            color: colors.danger,
            fontSize: '14px',
          }}>
            {error}
          </div>
        ) : runs.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: colors.textMuted }}>
            No cron runs match these filters.
          </div>
        ) : (
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: '14px',
          }}>
            <thead>
              <tr style={{ backgroundColor: colors.lightGray, borderBottom: `1px solid ${colors.mediumGray}` }}>
                <th style={thStyle}>Cron name</th>
                <th style={thStyle}>Trigger</th>
                <th style={thStyle}>Started</th>
                <th style={thStyle}>Duration</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Error</th>
                <th style={{ ...thStyle, width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run, idx) => {
                const isOpen = expanded.has(run.run_id);
                const isTbOpen = tracebackOpen.has(run.run_id);
                return (
                  <React.Fragment key={run.run_id}>
                    <tr
                      onClick={() => toggleExpanded(run.run_id)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: `1px solid ${colors.mediumGray}`,
                        backgroundColor: isOpen ? `${colors.accent}08` : colors.white,
                      }}
                    >
                      <td style={tdStyle}>
                        <code style={{ fontSize: '13px', color: colors.darkText }}>{run.cron_name}</code>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: '12px', color: colors.textMuted }}>
                          {run.trigger_source}
                        </span>
                      </td>
                      <td style={tdStyle}>{formatTimestamp(run.started_at)}</td>
                      <td style={tdStyle}>{formatDuration(run.duration_ms)}</td>
                      <td style={tdStyle}><StatusBadge status={run.status} /></td>
                      <td style={{ ...tdStyle, color: colors.textMuted, fontSize: '12px', maxWidth: '320px' }}>
                        {truncate(run.error_message, 80)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center', color: colors.textMuted }}>
                        {isOpen ? '▾' : '▸'}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr style={{ backgroundColor: `${colors.accent}05` }}>
                        <td colSpan={7} style={{ padding: '16px 24px', borderBottom: `1px solid ${colors.mediumGray}` }}>
                          <DetailGrid run={run} isTracebackOpen={isTbOpen} onToggleTraceback={() => toggleTraceback(run.run_id)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination footer */}
        {!loading && !error && runs.length > 0 && (
          <div style={{
            padding: '12px 20px',
            borderTop: `1px solid ${colors.mediumGray}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '13px', color: colors.textMuted,
          }}>
            <div>
              Showing {(pagination.page - 1) * pagination.page_size + 1}
              {' to '}
              {Math.min(pagination.page * pagination.page_size, pagination.total)}
              {' of '}
              {pagination.total}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.page <= 1}
                style={pageBtnStyle(pagination.page <= 1)}
              >
                ← Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={pagination.page >= pagination.total_pages}
                style={pageBtnStyle(pagination.page >= pagination.total_pages)}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// --- Small subcomponents ---

function FilterField({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{
        fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em',
        fontWeight: 600, color: colors.textMuted,
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function DetailGrid({ run, isTracebackOpen, onToggleTraceback }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
      {/* Metadata row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', fontSize: '13px' }}>
        <DetailMeta label="run_id" value={run.run_id} mono />
        <DetailMeta label="ended_at" value={formatTimestamp(run.ended_at)} />
        <DetailMeta label="company_id" value={run.company_id ?? 'None'} />
        <DetailMeta label="correlation_id" value={run.correlation_id || 'None'} mono />
      </div>

      {/* Error message (full, if present) */}
      {run.error_message && (
        <div>
          <SectionLabel>error_message</SectionLabel>
          <div style={{
            padding: '8px 12px', backgroundColor: `${colors.danger}10`,
            borderLeft: `3px solid ${colors.danger}`, borderRadius: '4px',
            fontSize: '13px', color: colors.darkText, whiteSpace: 'pre-wrap',
            wordBreak: 'break-word', fontFamily: 'Menlo, Consolas, monospace',
          }}>
            {run.error_message}
          </div>
        </div>
      )}

      {/* Traceback (collapsed by default) */}
      {run.error_traceback && (
        <div>
          <button
            onClick={onToggleTraceback}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em',
              fontWeight: 600, color: colors.textMuted,
            }}
          >
            {isTracebackOpen ? '▾' : '▸'} error_traceback
          </button>
          {isTracebackOpen && (
            <pre style={{
              marginTop: '8px', padding: '8px 12px',
              backgroundColor: colors.lightGray, borderRadius: '4px',
              fontSize: '12px', fontFamily: 'Menlo, Consolas, monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: colors.darkText,
              maxHeight: '300px', overflow: 'auto',
            }}>
              {run.error_traceback}
            </pre>
          )}
        </div>
      )}

      {/* Side effects */}
      <div>
        <SectionLabel>side_effects</SectionLabel>
        {renderSideEffects(run.side_effects)}
      </div>
    </div>
  );
}

function DetailMeta({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <span style={{
        fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em',
        fontWeight: 600, color: colors.textMuted,
      }}>{label}</span>
      <span style={{
        fontSize: '13px', color: colors.darkText,
        fontFamily: mono ? 'Menlo, Consolas, monospace' : 'inherit',
      }}>{value}</span>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em',
      fontWeight: 600, color: colors.textMuted, marginBottom: '6px',
    }}>
      {children}
    </div>
  );
}

// --- Styles ---

const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 600,
  color: colors.textMuted,
};

const tdStyle = {
  padding: '12px 16px',
  color: colors.darkText,
  verticalAlign: 'middle',
};

const selectStyle = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: `1px solid ${colors.mediumGray}`,
  backgroundColor: colors.white,
  fontSize: '14px',
  color: colors.darkText,
  cursor: 'pointer',
  minWidth: '180px',
};

const pageBtnStyle = (disabled) => ({
  padding: '6px 12px',
  borderRadius: '6px',
  border: `1px solid ${colors.mediumGray}`,
  backgroundColor: colors.white,
  color: disabled ? colors.textMuted : colors.darkText,
  fontSize: '13px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

export default AdminCronRuns;
